import { type StructuredNoteSchema } from '@/components/tiptap/types';
import { type SectionMappingLink, type TabType } from '@heidi/session/contracts';

import {
  type components,
  type EHRProvider,
  type TemplateCategory,
} from '@heidi/schema/ml-scribe';
import { Country } from '@heidi/utils/countries';
import { type z } from 'zod';

export type HardwareDeviceResponse = components['schemas']['HardwareDeviceResponse'];
export type LinkHardwareDeviceRequest =
  components['schemas']['LinkHardwareDeviceRequest'];

export enum AppType {
  Web = 'Web',
  Desktop = 'Desktop',
  AndroidApp = 'AndroidApp',
  iOSApp = 'iOSApp',
}

/**
 * The `creation_platform` discriminated union accepted by `POST /sessions/`.
 * Sourced from the generated backend schema (single source of truth) instead
 * of hand-written enums — built via `getSessionCreationPlatform`.
 */
export type CreationPlatform = NonNullable<CreateSessionRequest['creation_platform']>;

// `NotificationName` STOOD HERE and moved to `@heidi/workspace/contracts` with
// its only consumer, the notifications settings form. Deleted rather than
// re-exported: this barrel is imported eagerly across the app, so a re-export
// would buy a name with zero consumers at the price of a new eager edge into a
// package. The old path's name-intact rule governs the five settings modules
// the screens were served from, not an enum whose importer moved with it.

export type CreateLetterFromDocumentRequest =
  components['schemas']['CreateLetterFromDocumentRequest'];
export type CreateSessionRequest =
  components['schemas']['domains__sessions__schemas__CreateSessionRequest'];
// Imported, not forwarded: the name below types the template-section link
// that stays app-side.

export type ScheduleUploadView = components['schemas']['ScheduleUploadView'];

export type SingleTranscriptResponse =
  components['schemas']['AudioSourceTranscriptResponse'];

export type DictationEnabledSettingsRequest =
  components['schemas']['DictationEnabledSettingsRequest'];
export type DictationEnabledSettingsResponse =
  components['schemas']['DictationEnabledSettingsResponse'];

export type ExtensionTask = components['schemas']['ExtensionTask'];

export type ModalViewAction = components['schemas']['ModalViewAction'];
export type ModalView = components['schemas']['ModalView'];
export type UpdateAutoLinkIntentRequest =
  components['schemas']['UpdateAutoLinkIntentRequest'];
export type UpdateAutoExtractIntentRequest =
  components['schemas']['UpdateAutoExtractIntentRequest'];
export type GenerateDocumentContentRequestV2 =
  components['schemas']['GenerateDocumentContentRequestV2'];
// TODO: Remove extension after backend PR #9446 merges and pnpm schema is re-run
export type ShareConsultNotesResponse =
  components['schemas']['ShareConsultNotesResponse'];
export type ShareDocumentThroughEmailRequestV2 =
  components['schemas']['ShareDocumentThroughEmailRequestV2'];
export type ShareDocumentResponseV2 = components['schemas']['ShareDocumentResponseV2'];
export type ShareConsultNoteRequest = components['schemas']['ShareConsultNoteRequest'];
export type GenerateConsultNoteRequest =
  components['schemas']['GenerateConsultNoteRequest'];
export type IntegrationRequest = Omit<
  components['schemas']['database__mongo__base__IntegrationRequest'],
  'ehr_provider'
> & {
  ehr_provider: EHRProvider;
};

export type EHRProfileResponse = components['schemas']['EHRProfileResponse'];
export type CreateEHRProfileRequest = components['schemas']['CreateEHRProfileAPIRequest'];

/* EHR launch profile API */
export type UserEHRLaunchProfile = components['schemas']['UserEHRLaunchProfile'];

// Organization related types
export type InviteUsersToTeamResponse =
  components['schemas']['InviteUsersToTeamResponse'];

export type UserRole = components['schemas']['UserRole'];

export type PriceView = components['schemas']['PriceView'];

export type ListRecordingsResponse = components['schemas']['GetRecordingsResponse'];

export type FeedbackRequest = components['schemas']['FeedbackFormRequest'];
export type { FeedbackTags } from '@heidi/growth/contracts';

export type FeedbackEventTrackBaseProps = {
  feedbackCategory: TabType | undefined;
  noteCategory: TemplateCategory | TabType;
  userId: string;
  noteId: string;
  timestamp: string;
  documentName?: string;
};

export type FindReplaceContent = {
  [key: string]: string;
} | null;

export type CreateUserInmailRequest = components['schemas']['CreateUserInmailRequest'];

export type UpdateConsultNoteRequest = components['schemas']['UpdateConsultNoteRequest'];
export type UpdateConsultNoteResponse =
  components['schemas']['UpdateConsultNoteResponse'];

export type UpdateSessionContextDocumentsRequest =
  components['schemas']['UpdateSessionContextDocumentsRequest'];
export type DeleteSessionContextDocumentsRequest =
  components['schemas']['DeleteSessionContextDocumentsRequest'];
export type DeleteSessionContextDocumentsResponse =
  components['schemas']['DeleteSessionContextDocumentsResponse'];

export type SessionClinicalEntity = components['schemas']['CodedClinicalEntity'];

export type GetEHRLaunchAuthorizationURLResponse =
  components['schemas']['GetEHRLaunchAuthorizationURLResponse'];

export type PermissionValue = components['schemas']['PermissionValue'];

export enum NotificationType {
  SESSION_EXPIRING = 'session_expiring',
  NEW_INVITATION = 'new_invitation',
  ROLE_CHANGE = 'role_change',
  SESSION_SHARED = 'session_shared',
  TASK_ASSIGNED = 'task_assigned',
  HCP_VERIFICATION_NEEDED = 'hcp_verification_needed',
  HCP_VERIFICATION_VERIFIED = 'hcp_verification_verified',
}

export type TranscriptLiveResponse =
  components['schemas']['GetSessionLiveTranscriptResponse'];

export type LiveTranscriptions = components['schemas']['LiveTranscriptResponse'];

export type RecordingChunk = components['schemas']['ChunkedRecordingWithTranscript'];

export type SingleChunk = components['schemas']['Chunk'];

export type CreateLiveTranscriptionResponse =
  components['schemas']['CreateLiveTranscriptionResponse'];

export type PatientContextLLMResponse =
  components['schemas']['PatientContextLLMResponse'];

// E/M Level Determinator types — aliased from generated schema
export type EmMdmResult = components['schemas']['EmMdmResponse'];
export type EmAvailableCode = components['schemas']['EmCodeOption'];

// Export Assets (header/footer logos and signatures)
export type ExportAssetUrlResponse = components['schemas']['ExportAssetUrlResponse'];

// Call related types
export type CallSummaryView = components['schemas']['CallSummaryView'];
export type CallStatus = components['schemas']['CallStatus'];
export type AgentResponse = components['schemas']['AgentResponse'];
export type UpdateAgentRequest = components['schemas']['UpdateAgentRequest'];
export type CallDirection = components['schemas']['CallDirection'];
export type CallContextValue = components['schemas']['CallContextValue'];
export type CallContextValues = components['schemas']['CallContextValues'];

// Inbox / Conversations
export type ConversationSummaryView = components['schemas']['ConversationSummaryView'];
export type LastMessageView = components['schemas']['LastMessageView'];
export type PatientMatchView = components['schemas']['PatientMatchView'];
export type AgentSummaryView = components['schemas']['AgentSummaryView'];
export type TranscriptTurnView = components['schemas']['TranscriptTurnView'];

// Patients (calls)
export type ReminderRecordView = components['schemas']['ReminderRecordView'];
export type CsvUploadRequest = components['schemas']['CsvUploadRequest'];

export type {
  PersonalizationData,
  PersonalizationInsight,
} from '@heidi/templates/contracts';
export type PersonalizationCategory = components['schemas']['PersonalizationCategory'];

/* Template Creation Record related START */

export type CreateVersionControlFromExampleRequest =
  components['schemas']['CreateVersionControlFromExampleRequest'];

/* Template Creation Record related END */

/* EHR Letter Templates */
export type ListLetterTemplatesResponse =
  components['schemas']['ListLetterTemplatesResponse'];
export type DocumentTemplate = components['schemas']['DocumentTemplate'];
export type LetterAttachmentListResponse =
  components['schemas']['LetterAttachmentListResponse'];
export type LetterAttachmentDownloadUrlResponse =
  components['schemas']['LetterAttachmentDownloadUrlResponse'];
export type LetterAttachmentResponse = components['schemas']['LetterAttachmentResponse'];
export type LetterContentSnapshotTimelineEventResponse =
  components['schemas']['LetterContentSnapshotTimelineEventResponse'];
export type LetterDeliveryDetails = components['schemas']['LetterDeliveryDetails'];
export type LetterElectronicallySignedSignature =
  components['schemas']['LetterElectronicallySignedSignature'];
export type LetterTimelineEventListResponse =
  components['schemas']['LetterTimelineEventListResponse'];
export type LetterTimelineEventRecordResponse =
  components['schemas']['LetterTimelineEventRecordResponse'];
export type LetterResponse = components['schemas']['LetterResponse'];
export type LetterSignatureUser = components['schemas']['LetterSignatureUser'];
export type SendLetterRequest = components['schemas']['SendLetterRequest'];
export type SendLetterResponse = components['schemas']['SendLetterResponse'];
export type SignLetterRequest = components['schemas']['SignLetterRequest'];
export type UpdateLetterRequest = components['schemas']['UpdateLetterRequest'];
export type LetterSealedMessageDeliveryRecipient =
  components['schemas']['LetterSealedMessageDeliveryRecipient'];
export type LetterEmailRecipient = components['schemas']['LetterEmailRecipient'];
export type LetterDirectoryRecipient = components['schemas']['LetterDirectoryRecipient'];
export type LetterFaxRecipient = components['schemas']['LetterFaxRecipient'];
export type RecipientSearchResponse = components['schemas']['RecipientSearchResponse'];
export type DirectoryRecipientSearchResultResponse =
  components['schemas']['DirectoryRecipientSearchResultResponse'];
export type SealedMessageDeliveryRecipientSearchResultResponse =
  components['schemas']['SealedMessageDeliveryRecipientSearchResultResponse'];
export type LetterSendTimelineEventResponse =
  components['schemas']['LetterSendTimelineEventResponse'];

export type ConsultNoteResponseV2 = components['schemas']['ConsultNoteResponseV2'];
export type GetConsultNotesResponse = components['schemas']['GetConsultNotesResponse'];
export type GetConsultNoteTimelineResponse =
  components['schemas']['GetConsultNoteTimelineResponse'];

/**
 * INT-4603 template-section-mapping types. Hand-defined to match BE PR #10109
 * (apis/v2/integrations/template_section_mappings/schemas.py); will be replaced
 * by the generated OpenAPI shapes once the schema is regenerated.
 */
export type TemplateSectionLink = SectionMappingLink;

export type ConsultNoteLinkedSectionContent = {
  content: string;
  linked_ehr_section_id: string;
};

/* Identity Broker related types */

/**
 * Response of `POST /api/v1/identity-broker/assign-apple-migration-ticket`
 * (oscerai/ml-scribe#11078 / MUL-2093). Hand-written until the endpoint ships
 * in the generated OpenAPI spec; `ticket_id` is the UUIDv7 to hand to Auth0
 * and is non-null only for the two ticket statuses.
 */
export interface AssignAppleMigrationTicketResponse {
  status:
    | 'ticket_created'
    | 'ticket_refreshed'
    | 'already_migrated'
    | 'migration_in_progress'
    | 'migration_failed'
    | 'no_migration_needed';
  ticket_id: string | null;
}

// export const JSONTemplateSchema = z
//   .object({
//     globalInstructions: z.string().default(''),
//     sections: z
//       .array(
//         z.object({
//           heading: z.string(),
//           showHeading: z.boolean(),
//           template: z.string().nullish(),
//           content: z.string().nullish(),
//           linked_ehr_section_id: z.string().nullish(),
//           sectionInstructions: z.string().nullable(),
//         })
//       )
//       .nullish(),
//   })
//   .default({
//     globalInstructions: '',
//     sections: [],
//   });

// export type TemplateJSON = z.infer<typeof JSONTemplateSchema>;

// export type TemplateLinkedSectionContent = NonNullable<
//   z.infer<typeof JSONTemplateSchema>['sections']
// >[number];

export type StructuredNote = z.infer<typeof StructuredNoteSchema>;
export type ConvertPlainTextNoteToSectionalNoteResponse =
  components['schemas']['ConvertPlainTextNoteToSectionalNoteResponse'];

export type RefreshAppointmentTypesResponse =
  components['schemas']['RefreshAppointmentTypesResponse'];
export type RefreshCliniciansResponse =
  components['schemas']['RefreshCliniciansResponse'];
export type RefreshClinicsResponse = components['schemas']['RefreshClinicsResponse'];

// Ehr Launch latest note
export type GetEHRLatestNoteResponse = components['schemas']['GetEHRLatestNoteResponse'];

export type IntegrationOnboardingStepExecutionRequest =
  components['schemas']['IntegrationOnboardingStepExecutionRequest'];
export type IntegrationOnboardingStepExecutionResponse =
  components['schemas']['IntegrationOnboardingStepExecutionResponse'];
export type PendingIntegrationOnboardingExecutionStep =
  components['schemas']['PendingIntegrationOnboardingExecutionStep'];
export type OnboardingSessionStatus = components['schemas']['OnboardingSessionStatus'];
export type GetMobileRequest = components['schemas']['GetMobileRequest'];

export type SeenModals = {
  [key: string]: ModalView;
};

export type ChatResponseBase = components['schemas']['ChatResponse'];
export type ChatResponse = ChatResponseBase & {
  document_size_bytes?: number;
};
export type ChatMessageResponse = components['schemas']['ChatMessageResponse'];
export type ChatListItem = components['schemas']['ChatListItem'];
export type ChatMessageMetadataResponse =
  components['schemas']['ChatMessageMetadataResponse'];
export type CreateChatRequest = components['schemas']['CreateChatRequest'];
export type LocalStorageChatMessageRequest =
  components['schemas']['LocalStorageChatMessageRequest'];
export type LocalStorageChatRequest = components['schemas']['LocalStorageChatRequest'];

// Note: this is a hack for now - ideally backend should be exporting these enums
// And also this is just being used to make sure the user currency and country

export enum Currency {
  USD = 'usd',
  AUD = 'aud',
  CAD = 'cad',
  CHF = 'chf',
  CNY = 'cny',
  EUR = 'eur',
  GBP = 'gbp',
  HKD = 'hkd',
  IDR = 'idr',
  JPY = 'jpy',
  MYR = 'myr',
  NZD = 'nzd',
  SGD = 'sgd',
  ZAR = 'zar',
  INR = 'inr',
  KRW = 'krw',
  PHP = 'php',
  PKR = 'pkr',
  PLN = 'pln',
  SAR = 'sar',
}

export const countryCurrency: Record<Country, Currency> = {
  // AU region countries use AUD
  [Country.AUSTRALIA]: Currency.AUD,
  [Country.BANGLADESH]: Currency.AUD,
  [Country.BHUTAN]: Currency.AUD,
  [Country.BRUNEI_DARUSSALAM]: Currency.AUD,
  [Country.CAMBODIA]: Currency.AUD,
  [Country.CHRISTMAS_ISLAND]: Currency.AUD,
  [Country.COCOS_KEELING_ISLANDS]: Currency.AUD,
  [Country.COOK_ISLANDS]: Currency.AUD,
  [Country.FIJI]: Currency.AUD,
  [Country.FRENCH_SOUTHERN_TERRITORIES]: Currency.AUD,
  [Country.GUAM]: Currency.AUD,
  [Country.INDIA]: Currency.AUD,
  [Country.KIRIBATI]: Currency.AUD,
  [Country.KOREA_DEMOCRATIC_PEOPLES_REPUBLIC]: Currency.AUD,
  [Country.KOREA_REPUBLIC]: Currency.AUD,
  [Country.LAO_PEOPLES_DEMOCRATIC_REPUBLIC]: Currency.AUD,
  [Country.MACAO]: Currency.AUD,
  [Country.MALDIVES]: Currency.AUD,
  [Country.MARSHALL_ISLANDS]: Currency.AUD,
  [Country.MICRONESIA]: Currency.AUD,
  [Country.MONGOLIA]: Currency.AUD,
  [Country.MYANMAR]: Currency.AUD,
  [Country.NAURU]: Currency.AUD,
  [Country.NEPAL]: Currency.AUD,
  [Country.NEW_CALEDONIA]: Currency.AUD,
  [Country.NIUE]: Currency.AUD,
  [Country.NORFOLK_ISLAND]: Currency.AUD,
  [Country.NORTHERN_MARIANA_ISLANDS]: Currency.AUD,
  [Country.PAKISTAN]: Currency.AUD,
  [Country.PALAU]: Currency.AUD,
  [Country.PAPUA_NEW_GUINEA]: Currency.AUD,
  [Country.PHILIPPINES]: Currency.AUD,
  [Country.PITCAIRN]: Currency.AUD,
  [Country.SAMOA]: Currency.AUD,
  [Country.SOLOMON_ISLANDS]: Currency.AUD,
  [Country.SOUTH_GEORGIA_AND_SANDWICH_ISLANDS]: Currency.AUD,
  [Country.SRI_LANKA]: Currency.AUD,
  [Country.TAIWAN]: Currency.AUD,
  [Country.THAILAND]: Currency.AUD,
  [Country.TOKELAU]: Currency.AUD,
  [Country.TONGA]: Currency.AUD,
  [Country.TUVALU]: Currency.AUD,
  [Country.VANUATU]: Currency.AUD,
  [Country.VIETNAM]: Currency.AUD,
  [Country.OTHERS]: Currency.AUD,
  // AU region alternate currency CNY, HKD, IDR, JPY, MYR, NZD, SGD
  [Country.CHINA]: Currency.CNY,
  [Country.HONG_KONG]: Currency.HKD,
  [Country.TIMOR_LESTE]: Currency.IDR,
  [Country.INDONESIA]: Currency.IDR,
  [Country.JAPAN]: Currency.JPY,
  [Country.MALAYSIA]: Currency.MYR,
  [Country.NEW_ZEALAND]: Currency.NZD,
  [Country.SINGAPORE]: Currency.SGD,
  // CA region countries use CAD
  [Country.CANADA]: Currency.CAD,
  // EU region countries use EUR
  [Country.AFGHANISTAN]: Currency.EUR,
  [Country.ALBANIA]: Currency.EUR,
  [Country.ALGERIA]: Currency.EUR,
  [Country.ANDORRA]: Currency.EUR,
  [Country.ANGOLA]: Currency.EUR,
  [Country.ARMENIA]: Currency.EUR,
  [Country.AUSTRIA]: Currency.EUR,
  [Country.AZERBAIJAN]: Currency.EUR,
  [Country.BAHRAIN]: Currency.EUR,
  [Country.BELARUS]: Currency.EUR,
  [Country.BELGIUM]: Currency.EUR,
  [Country.BENIN]: Currency.EUR,
  [Country.BOSNIA_AND_HERZEGOVINA]: Currency.EUR,
  [Country.BOTSWANA]: Currency.EUR,
  [Country.BULGARIA]: Currency.EUR,
  [Country.BURKINA_FASO]: Currency.EUR,
  [Country.BURUNDI]: Currency.EUR,
  [Country.CAMEROON]: Currency.EUR,
  [Country.CAPE_VERDE]: Currency.EUR,
  [Country.CENTRAL_AFRICAN_REPUBLIC]: Currency.EUR,
  [Country.CHAD]: Currency.EUR,
  [Country.COMOROS]: Currency.EUR,
  [Country.CONGO_DEMOCRATIC_REPUBLIC]: Currency.EUR,
  [Country.CONGO_REPUBLIC]: Currency.EUR,
  [Country.COTE_DIVOIRE]: Currency.EUR,
  [Country.CROATIA]: Currency.EUR,
  [Country.CYPRUS]: Currency.EUR,
  [Country.CZECH_REPUBLIC]: Currency.EUR,
  [Country.DENMARK]: Currency.EUR,
  [Country.DJIBOUTI]: Currency.EUR,
  [Country.EGYPT]: Currency.EUR,
  [Country.EQUATORIAL_GUINEA]: Currency.EUR,
  [Country.ERITREA]: Currency.EUR,
  [Country.ESTONIA]: Currency.EUR,
  [Country.ETHIOPIA]: Currency.EUR,
  [Country.FAROE_ISLANDS]: Currency.EUR,
  [Country.FINLAND]: Currency.EUR,
  [Country.FRANCE]: Currency.EUR,
  [Country.GABON]: Currency.EUR,
  [Country.GAMBIA]: Currency.EUR,
  [Country.GEORGIA]: Currency.EUR,
  [Country.GERMANY]: Currency.EUR,
  [Country.GHANA]: Currency.EUR,
  [Country.GIBRALTAR]: Currency.EUR,
  [Country.GREECE]: Currency.EUR,
  [Country.GREENLAND]: Currency.EUR,
  [Country.GUERNSEY]: Currency.EUR,
  [Country.GUINEA]: Currency.EUR,
  [Country.GUINEA_BISSAU]: Currency.EUR,
  [Country.HOLY_SEE]: Currency.EUR,
  [Country.HUNGARY]: Currency.EUR,
  [Country.ICELAND]: Currency.EUR,
  [Country.IRAN]: Currency.EUR,
  [Country.IRAQ]: Currency.EUR,
  [Country.IRELAND]: Currency.EUR,
  [Country.ISLE_OF_MAN]: Currency.EUR,
  [Country.ISRAEL]: Currency.EUR,
  [Country.ITALY]: Currency.EUR,
  [Country.JERSEY]: Currency.EUR,
  [Country.JORDAN]: Currency.EUR,
  [Country.KAZAKHSTAN]: Currency.EUR,
  [Country.KENYA]: Currency.EUR,
  [Country.KUWAIT]: Currency.EUR,
  [Country.KYRGYZSTAN]: Currency.EUR,
  [Country.LATVIA]: Currency.EUR,
  [Country.LEBANON]: Currency.EUR,
  [Country.LIBERIA]: Currency.EUR,
  [Country.LIBYA]: Currency.EUR,
  [Country.LITHUANIA]: Currency.EUR,
  [Country.LUXEMBOURG]: Currency.EUR,
  [Country.MACEDONIA]: Currency.EUR,
  [Country.MADAGASCAR]: Currency.EUR,
  [Country.MALAWI]: Currency.EUR,
  [Country.MALI]: Currency.EUR,
  [Country.MALTA]: Currency.EUR,
  [Country.MAURITANIA]: Currency.EUR,
  [Country.MAURITIUS]: Currency.EUR,
  [Country.MAYOTTE]: Currency.EUR,
  [Country.MOLDOVA]: Currency.EUR,
  [Country.MONACO]: Currency.EUR,
  [Country.MONTENEGRO]: Currency.EUR,
  [Country.MOROCCO]: Currency.EUR,
  [Country.MOZAMBIQUE]: Currency.EUR,
  [Country.NETHERLANDS]: Currency.EUR,
  [Country.NIGER]: Currency.EUR,
  [Country.NIGERIA]: Currency.EUR,
  [Country.NORWAY]: Currency.EUR,
  [Country.OMAN]: Currency.EUR,
  [Country.PALESTINE]: Currency.EUR,
  [Country.POLAND]: Currency.EUR,
  [Country.PORTUGAL]: Currency.EUR,
  [Country.QATAR]: Currency.EUR,
  [Country.REUNION]: Currency.EUR,
  [Country.ROMANIA]: Currency.EUR,
  [Country.RUSSIAN_FEDERATION]: Currency.EUR,
  [Country.RWANDA]: Currency.EUR,
  [Country.SAINT_BARTHELEMY]: Currency.EUR,
  [Country.SAINT_HELENA]: Currency.EUR,
  [Country.SAINT_MARTIN]: Currency.EUR,
  [Country.SAINT_PIERRE_AND_MIQUELON]: Currency.EUR,
  [Country.SAN_MARINO]: Currency.EUR,
  [Country.SAO_TOME_AND_PRINCIPE]: Currency.EUR,
  [Country.SAUDI_ARABIA]: Currency.EUR,
  [Country.SENEGAL]: Currency.EUR,
  [Country.SERBIA]: Currency.EUR,
  [Country.SEYCHELLES]: Currency.EUR,
  [Country.SIERRA_LEONE]: Currency.EUR,
  [Country.SLOVAKIA]: Currency.EUR,
  [Country.SLOVENIA]: Currency.EUR,
  [Country.SOMALIA]: Currency.EUR,
  [Country.SOUTH_SUDAN]: Currency.EUR,
  [Country.SPAIN]: Currency.EUR,
  [Country.SUDAN]: Currency.EUR,
  [Country.SVALBARD_AND_JAN_MAYEN]: Currency.EUR,
  [Country.SWAZILAND]: Currency.EUR,
  [Country.SWEDEN]: Currency.EUR,
  [Country.SYRIAN_ARAB_REPUBLIC]: Currency.EUR,
  [Country.TAJIKISTAN]: Currency.EUR,
  [Country.TANZANIA]: Currency.EUR,
  [Country.TOGO]: Currency.EUR,
  [Country.TUNISIA]: Currency.EUR,
  [Country.TURKEY]: Currency.EUR,
  [Country.TURKMENISTAN]: Currency.EUR,
  [Country.UGANDA]: Currency.EUR,
  [Country.UKRAINE]: Currency.EUR,
  [Country.UNITED_ARAB_EMIRATES]: Currency.EUR,
  [Country.UZBEKISTAN]: Currency.EUR,
  [Country.WESTERN_SAHARA]: Currency.EUR,
  [Country.YEMEN]: Currency.EUR,
  [Country.ZAMBIA]: Currency.EUR,
  [Country.ZIMBABWE]: Currency.EUR,
  // EU region alternative currencies CHF, ZAR
  [Country.SWITZERLAND]: Currency.CHF,
  [Country.LIECHTENSTEIN]: Currency.CHF,
  [Country.SOUTH_AFRICA]: Currency.ZAR,
  [Country.NAMIBIA]: Currency.ZAR,
  [Country.LESOTHO]: Currency.ZAR,
  // UK region countries use GBP
  [Country.BRITISH_INDIAN_OCEAN_TERRITORY]: Currency.GBP,
  [Country.UNITED_KINGDOM]: Currency.GBP,
  [Country.VIRGIN_ISLANDS_BRITISH]: Currency.GBP,
  // US region countries use USD
  [Country.AMERICAN_SAMOA]: Currency.USD,
  [Country.ANGUILLA]: Currency.USD,
  [Country.ANTARCTICA]: Currency.USD,
  [Country.ANTIGUA_AND_BARBUDA]: Currency.USD,
  [Country.ARGENTINA]: Currency.USD,
  [Country.ARUBA]: Currency.USD,
  [Country.BAHAMAS]: Currency.USD,
  [Country.BARBADOS]: Currency.USD,
  [Country.BELIZE]: Currency.USD,
  [Country.BERMUDA]: Currency.USD,
  [Country.BOLIVIA]: Currency.USD,
  [Country.BRAZIL]: Currency.USD,
  [Country.CAYMAN_ISLANDS]: Currency.USD,
  [Country.CHILE]: Currency.USD,
  [Country.COLOMBIA]: Currency.USD,
  [Country.COSTA_RICA]: Currency.USD,
  [Country.CUBA]: Currency.USD,
  [Country.CURACAO]: Currency.USD,
  [Country.DOMINICA]: Currency.USD,
  [Country.DOMINICAN_REPUBLIC]: Currency.USD,
  [Country.ECUADOR]: Currency.USD,
  [Country.EL_SALVADOR]: Currency.USD,
  [Country.FALKLAND_ISLANDS]: Currency.USD,
  [Country.FRENCH_GUIANA]: Currency.USD,
  [Country.GRENADA]: Currency.USD,
  [Country.GUADELOUPE]: Currency.USD,
  [Country.GUATEMALA]: Currency.USD,
  [Country.GUYANA]: Currency.USD,
  [Country.HAITI]: Currency.USD,
  [Country.HONDURAS]: Currency.USD,
  [Country.JAMAICA]: Currency.USD,
  [Country.MARTINIQUE]: Currency.USD,
  [Country.MEXICO]: Currency.USD,
  [Country.MONTSERRAT]: Currency.USD,
  [Country.NICARAGUA]: Currency.USD,
  [Country.PANAMA]: Currency.USD,
  [Country.PARAGUAY]: Currency.USD,
  [Country.PERU]: Currency.USD,
  [Country.PUERTO_RICO]: Currency.USD,
  [Country.SAINT_KITTS_AND_NEVIS]: Currency.USD,
  [Country.SAINT_LUCIA]: Currency.USD,
  [Country.SAINT_VINCENT_AND_GRENADINES]: Currency.USD,
  [Country.SINT_MAARTEN]: Currency.USD,
  [Country.SURINAME]: Currency.USD,
  [Country.TRINIDAD_AND_TOBAGO]: Currency.USD,
  [Country.TURKS_AND_CAICOS_ISLANDS]: Currency.USD,
  [Country.UNITED_STATES]: Currency.USD,
  [Country.URUGUAY]: Currency.USD,
  [Country.VENEZUELA]: Currency.USD,
  [Country.VIRGIN_ISLANDS_US]: Currency.USD,
  [Country.WALLIS_AND_FUTUNA]: Currency.USD,
};
